import { BaseModel } from '../base.model';
import { MappingProperty } from '../functions';
import { LookupBase } from 'ag-common-lib/lib/models/utils/lookup-base.model';
import { LookupKeys } from 'ag-common-lib/lib/models/crm/lookup.model';
import { Headshot } from 'ag-common-lib/lib/models/utils/headshot.model';
import { AgentBaseKeyMapping } from './agent.model';

export enum PolicyTransactionKeys {
  carrierName = 'carrier_name',
  agencyNumber = 'agency_number',
  mgaId = 'mga_id',
  agentNumber = 'agent_number',
  agentId = 'agent_id',
  agentName = 'agent_name',
  transDate = 'transdate',
  policyNumber = 'policy_number',
  insuredName = 'insured_name',
  productName = 'product_name',
  productType = 'product_type',
  faceAmount = 'face_amount',
  lifePrem = 'life_prem',
  targetPrem = 'target_prem',
  excessPrem = 'excess_prem',
  annuity = 'annuity',
  policies = 'policies',
  weightedPrem = 'weighted_prem',
  recruiter = 'recruiter',
  count = 'count',
  year = 'year',
  week = 'week',
  hash = 'hash',
  // fields for runtime mapping
  mgaName = 'mga_name',
  agentDisplayName = 'agent_display_name',
  agentEmail = 'agent_email',
  policyDisplayType = 'policy_display_type',
  headshot = 'head_shot',
  agent = 'agent',
}

export class PolicyTransaction extends BaseModel {
  [PolicyTransactionKeys.carrierName]?: string;
  [PolicyTransactionKeys.agencyNumber]?: string;
  [PolicyTransactionKeys.mgaId]?: string;
  [PolicyTransactionKeys.agentNumber]?: string;
  [PolicyTransactionKeys.agentId]?: string;
  [PolicyTransactionKeys.agentName]?: string;
  [PolicyTransactionKeys.transDate]?: string;
  [PolicyTransactionKeys.policyNumber]?: string;
  [PolicyTransactionKeys.insuredName]?: string;
  [PolicyTransactionKeys.productName]?: string;
  [PolicyTransactionKeys.productType]?: string;
  [PolicyTransactionKeys.faceAmount]?: number = 0;
  [PolicyTransactionKeys.lifePrem]?: number = 0;
  [PolicyTransactionKeys.targetPrem]?: number = 0;
  [PolicyTransactionKeys.excessPrem]?: number = 0;
  [PolicyTransactionKeys.annuity]?: number = 0;
  [PolicyTransactionKeys.policies]?: number = 0;
  [PolicyTransactionKeys.weightedPrem]?: number = 0;
  [PolicyTransactionKeys.recruiter]?: string;
  [PolicyTransactionKeys.year]?: number;
  [PolicyTransactionKeys.week]?: number;
  [PolicyTransactionKeys.hash]?: string;
  [PolicyTransactionKeys.policyDisplayType]?: string;
  // fields for runtime mapping
  [PolicyTransactionKeys.mgaName]?: string;
  [PolicyTransactionKeys.agentDisplayName]?: string;
  [PolicyTransactionKeys.headshot]?: Headshot;
  [PolicyTransactionKeys.agent]?: any;
}

export const PolicyTransactionMapping: {
  [key: string]: MappingProperty;
} = {
  [PolicyTransactionKeys.carrierName]: {
    type: 'keyword',
    normalizer: 'folded_normalizer',
    fields: {
      raw: { type: 'keyword' },
    },
  },
  [PolicyTransactionKeys.agencyNumber]: { type: 'keyword' },
  [PolicyTransactionKeys.mgaId]: { type: 'keyword' },
  [PolicyTransactionKeys.agentNumber]: { type: 'keyword' },
  [PolicyTransactionKeys.agentId]: { type: 'keyword' },
  [PolicyTransactionKeys.agentName]: {
    type: 'keyword',
    normalizer: 'folded_normalizer',
    fields: {
      raw: { type: 'keyword' },
    },
  },
  [PolicyTransactionKeys.transDate]: { type: 'date', format: 'yyyy/MM/dd' },
  [PolicyTransactionKeys.policyNumber]: { type: 'keyword' },
  [PolicyTransactionKeys.insuredName]: {
    type: 'keyword',
    normalizer: 'folded_normalizer',
    fields: {
      raw: { type: 'keyword' },
    },
  },
  [PolicyTransactionKeys.productName]: {
    type: 'keyword',
    normalizer: 'folded_normalizer',
    fields: {
      raw: { type: 'keyword' },
    },
  },
  [PolicyTransactionKeys.recruiter]: {
    type: 'keyword',
    normalizer: 'folded_normalizer',
    fields: {
      raw: { type: 'keyword' },
    },
  },
  [PolicyTransactionKeys.hash]: { type: 'keyword' },
  [PolicyTransactionKeys.faceAmount]: { type: 'double' },
  [PolicyTransactionKeys.lifePrem]: { type: 'double' },
  [PolicyTransactionKeys.targetPrem]: { type: 'double' },
  [PolicyTransactionKeys.excessPrem]: { type: 'double' },
  [PolicyTransactionKeys.annuity]: { type: 'double' },
  [PolicyTransactionKeys.policies]: { type: 'double' },
  [PolicyTransactionKeys.weightedPrem]: { type: 'double' },
  [PolicyTransactionKeys.year]: { type: 'double' },
  [PolicyTransactionKeys.week]: { type: 'double' },
  [PolicyTransactionKeys.agent]: {
    properties: AgentBaseKeyMapping,
  },
};

export interface GroupedPolicyTransaction {
  key: string;
  [PolicyTransactionKeys.policyNumber]: string;
  [PolicyTransactionKeys.carrierName]: string;
  [PolicyTransactionKeys.insuredName]: string;
  [PolicyTransactionKeys.productName]: string;
  itemsCount: number;
  items: PolicyTransaction[];
}

export const POLICY_TRANSACTION_LOOKUP: LookupBase[] = [
  {
    [LookupKeys.value]: PolicyTransactionKeys.policyNumber,
    [LookupKeys.description]: 'Policy Number',
  },
  {
    [LookupKeys.value]: PolicyTransactionKeys.productName,
    [LookupKeys.description]: 'Product Name',
  },
  {
    [LookupKeys.value]: PolicyTransactionKeys.carrierName,
    [LookupKeys.description]: 'Carrier Name',
  },
  {
    [LookupKeys.value]: PolicyTransactionKeys.insuredName,
    [LookupKeys.description]: 'Insured Name',
  },
  {
    [LookupKeys.value]: PolicyTransactionKeys.transDate,
    [LookupKeys.description]: 'Transaction Date',
  },
  {
    [LookupKeys.value]: PolicyTransactionKeys.policies,
    [LookupKeys.description]: 'Policy Count',
  },
  {
    [LookupKeys.value]: PolicyTransactionKeys.weightedPrem,
    [LookupKeys.description]: 'Total Weight Premium',
  },
  {
    [LookupKeys.value]: PolicyTransactionKeys.agentName,
    [LookupKeys.description]: 'Agent Name',
  },
  {
    [LookupKeys.value]: PolicyTransactionKeys.mgaName,
    [LookupKeys.description]: 'MGA',
  },
];
